import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ExternalLink, ArrowLeft } from "lucide-react";

export default function SuperAdminNetworkDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [network, setNetwork] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get(`/networks/${id}`).then((r) => setNetwork(r.data));
    api.get(`/tenants?network_id=${id}`).then((r) => setTenants(r.data));
    api.get(`/products?network_id=${id}`).then((r) => setProducts(r.data));
  }, [id]);

  if (!network) return <AdminLayout><div className="p-10">Loading…</div></AdminLayout>;

  const vc = network.vertical_config || {};
  const terminology = vc.terminology || {};
  const statuses = vc.availability_statuses || [];
  const variantFields = vc.variant_custom_fields || [];

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="sa-network-detail">
        <Link to="/superadmin/networks" data-testid="net-detail-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Networks
        </Link>

        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-brand mb-2">{network.network_key}</div>
            <h1 className="font-heading font-bold text-5xl tracking-tight">{network.display_name}</h1>
            <div className="text-muted-foreground mt-2">{network.main_domain}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{network.status}</span>
          </div>
        </div>

        <Tabs defaultValue="vertical">
          <TabsList data-testid="net-detail-tabs">
            <TabsTrigger value="vertical" data-testid="tab-vertical">Vertical Config</TabsTrigger>
            <TabsTrigger value="tenants" data-testid="tab-tenants">Tenants ({tenants.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Prodotti ({products.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="vertical" className="mt-6 space-y-6">
            {/* Terminology */}
            <div className="bg-white border border-border rounded-sm p-6">
              <div className="overline text-brand mb-4">{t("sa_terminology")}</div>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(terminology).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-xs font-mono text-muted-foreground">{k}</span>
                    <span className="text-sm font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Statuses */}
            <div className="bg-white border border-border rounded-sm p-6">
              <div className="overline text-brand mb-4">{t("sa_statuses")}</div>
              <div className="space-y-2">
                {statuses.map((s) => (
                  <div key={s.key} className="flex items-center gap-4 py-2 border-b border-border last:border-0">
                    <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{s.label}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.key}</div>
                    </div>
                    {s.eta_text && <span className="text-xs text-muted-foreground">{s.eta_text}</span>}
                    {s.default_discount_pct && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-brand/10 text-brand font-medium">-{s.default_discount_pct}%</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Variant fields */}
            <div className="bg-white border border-border rounded-sm p-6">
              <div className="overline text-brand mb-4">{t("sa_custom_fields_v")}</div>
              <div className="space-y-2">
                {variantFields.map((f) => (
                  <div key={f.key} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <div className="font-medium text-sm">{f.label} {f.required && <span className="text-brand">*</span>}</div>
                      <div className="text-xs text-muted-foreground font-mono">{f.key} · {f.type}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            {vc.categories && (
              <div className="bg-white border border-border rounded-sm p-6">
                <div className="overline text-brand mb-4">{t("sa_categories")}</div>
                <div className="flex flex-wrap gap-2">
                  {vc.categories.map((c) => (
                    <span key={c} className="px-2.5 py-1 text-xs rounded-full bg-accent text-foreground font-medium">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tenants" className="mt-6">
            <div className="bg-white border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#FAFAFA] border-b border-border">
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Tenant</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Type</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Slug</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tn) => (
                    <tr key={tn.id} className="border-b border-border last:border-0" data-testid={`tenant-row-${tn.slug}`}>
                      <td className="px-5 py-4 font-medium">{tn.name}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${tn.type === "casa_madre" ? "bg-brand/10 text-brand" : "bg-blue-50 text-blue-700"}`}>{tn.type}</span>
                      </td>
                      <td className="px-5 py-4 text-sm font-mono text-muted-foreground">{tn.slug}</td>
                      <td className="px-5 py-4 text-sm">{tn.status}</td>
                      <td className="px-5 py-4 text-right">
                        {tn.type === "rivenditore" && (
                          <a
                            href={`/s/${tn.slug}`}
                            target="_blank" rel="noreferrer"
                            data-testid={`tenant-storefront-${tn.slug}`}
                            className="text-brand text-sm font-medium inline-flex items-center gap-1.5"
                          >
                            Vetrina <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="bg-white border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#FAFAFA] border-b border-border">
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Nome</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">SKU</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Categoria</th>
                    <th className="text-center px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Varianti</th>
                    <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Prezzo cons.</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0" data-testid={`product-row-${p.sku_madre}`}>
                      <td className="px-5 py-4 font-medium">{p.nome}</td>
                      <td className="px-5 py-4 text-sm font-mono text-muted-foreground">{p.sku_madre}</td>
                      <td className="px-5 py-4 text-sm">{p.categoria}</td>
                      <td className="px-5 py-4 text-center font-medium">{p.varianti_count}</td>
                      <td className="px-5 py-4 text-right font-medium">€ {p.prezzo_consigliato_retail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
